import dateRangePickerView from './dateRangePickerComponent.html';
import '../datePickerCommon.css';
import './dateRangePickerComponent.css';
import { DateRangeCalculator } from 'components/sharpr/date/dateRangeCalculator/DateRangeCalculator';
import { zIndexManager } from 'components/sharpr/zIndex/manager/zIndexManager';
import moment from 'moment';
import _ from 'lodash';

export function dateRangePickerComponent() {
	return {
		bindings: {
			model: '=',
			labelText: '@',
			labelFormat: '@',
			isOpen: '=?',
			align: '@?',
			presets: '<',
			onApply: '&?',
			onClose: '&?',
		},
		controllerAs: 'vm',
		controller: dateRangePickerComponentCtrl,
		template: dateRangePickerView,
	};
}

export class dateRangePickerComponentCtrl {
	$onInit() {
		this._initListeners();
		this._initCalendars();
		this._initRange();
		this._initAlignment();
		this._initPresets();
		this.zIndexTop = zIndexManager.next();
	}

	_initListeners() {
		this.onApply = this.onApply || function () {};
		this.onClose = this.onClose || function () {};
	}

	_initCalendars() {
		const model = this._getModel();
		this.startMonth = moment(model[0] || '-1 month').date(1);
		this.endMonth = moment(model[1] || 'today').date(1);
		this.startCalendar = this.getWeeks(this.startMonth);
		this.endCalendar = this.getWeeks(this.endMonth);
	}

	_initRange() {
		this.range = [];
		const model = this._getModel();
		if (model[1]) {
			this.range[1] = moment(model[1]);
		}
		if (model[0]) {
			this.range[0] = moment(model[0]);
			this.markRange();
		}
	}

	_getModel() {
		if (_.isArray(this.model)) {
			// array with [start, end]
			return this.model;
		} else {
			// object with .dateStart, .dateEnd
			return [this.model.dateStart, this.model.dateEnd];
		}
	}

	_setModel(values) {
		if (_.isArray(this.model)) {
			this.model[0] = values[0];
			this.model[1] = values[1];
		} else {
			this.model.dateStart = values[0];
			this.model.dateEnd = values[1];
		}
	}

	_initPresets() {
		if (this.presets == 'default') {
			this.presets = DateRangeCalculator.getPresets();
		}
	}

	_initAlignment() {
		const h = (this.align || '').match(/(left|center|right)/i);
		const v = (this.align || '').match(/(top|bottom)/i);
		this.hAlign = h ? h[1].toLowerCase() : 'right';
		this.vAlign = v ? v[1].toLowerCase() : 'bottom';
	}

	apply() {
		this._setModel([this.range[0].format('YYYY-MM-DD'), this.range[1].format('YYYY-MM-DD')]);
		this.isOpen = false;
		this._dispatchEvent('onApply');
	}

	toggle() {
		if (this.isOpen) {
			this.close();
		} else {
			this.zIndexTop = zIndexManager.next();
			this.isOpen = true;
		}
	}

	close() {
		if (this._dispatchEvent('onClose').defaultPrevented) {
			// event was canceled
			return;
		}
		this.isOpen = false;
	}

	_dispatchEvent(type, data = {}) {
		let event = new CustomEvent(type, {
			cancelable: true,
			target: this,
		});
		data.$event = event;
		this[type](data);
		return event;
	}

	fitCalendarsToRange() {
		this.startMonth = this.range[0].clone();
		this.startCalendar = this.getWeeks(this.startMonth);
		this.endMonth = this.range[1].clone();
		if (this.startMonth.format('YYYYMM') == this.range[1].format('YYYYMM')) {
			// range fits within one calendar month:
			// make second calendar one month ahead
			this.endMonth.add(1, 'month');
		}
		this.endCalendar = this.getWeeks(this.endMonth);
	}

	setRange(range) {
		this.range = [moment(range[0]), moment(range[1])];
		this.fitCalendarsToRange();
		this.markRange();
	}

	updateMonth(type, direction) {
		this[type + 'Month'].add(direction, 'month');
		this[type + 'Calendar'] = this.getWeeks(this[type + 'Month']);
		this.markRange();
	}

	setRangeIfValid(index, event) {
		let input = event.target;
		let date = moment(input.value);
		if (date.isValid()) {
			// set model and input value
			this.range[index] = date;
			this.setRange(this.range);
			input.value = date.format(this.labelFormat);
		} else {
			// revert input value to model value
			input.value = moment(this.range[index]).format(this.labelFormat);
		}
	}

	_eachDay(callback) {
		['start', 'end'].forEach(type => {
			this[type + 'Month'].weeks.forEach(days => {
				days.forEach(callback);
			});
		});
	}

	setAt(day) {
		if (this.range[0] && !this.range[1]) {
			// begin date but no end date:
			// set the end date in the range
			if (day.date < this.range[0]) {
				// we went backwards, e.g. picked end date before start date:
				// switch start and end
				this.range = [day.date.clone(), this.range[0]];
			} else {
				this.range[1] = day.date.clone();
				if (this.range[0].format('YYYYMM') != this.startMonth.format('YYYYMM')) {
					// User clicked on second calendar for start date and end date
					this.fitCalendarsToRange();
				}
			}
			this.markRange();
		} else {
			// first click or end date already set:
			// mark begin date
			this.range = [day.date.clone()];
			this.clearRange();
		}
	}

	clearRange() {
		let start = this.range[0].format('YYYYMMDD');
		this._eachDay(day => {
			day.classes[4] = '';
			day.classes[5] = '';
			day.classes[6] = '';
			if (day.ymd == start) {
				day.classes[5] = 'start-of-range';
			}
		});
	}

	markRange() {
		if (!this.range[0] || !this.range[1]) {
			// nothing to mark because we have no range yet
			return;
		}
		let start = this.range[0].format('YYYYMMDD');
		let end = this.range[1].format('YYYYMMDD');
		this._eachDay(day => {
			day.classes[4] = day.ymd >= start && day.ymd <= end ? 'in-range' : '';
			day.classes[5] = day.ymd == start ? 'start-of-range' : '';
			day.classes[6] = day.ymd == end ? 'end-of-range' : '';
		});
	}

	selectAt(day) {
		if (this.range[0] && !this.range[1]) {
			// we are in selection mode
			this.markSelection(day.date);
		} else {
			// not in selection mode; make sure nothing is selected
			this.clearSelection();
		}
	}

	markSelection(toDate) {
		let fromYmd = this.range[0].format('YYYYMMDD');
		let toYmd = toDate.format('YYYYMMDD');
		this._eachDay(day => {
			day.classes[7] = day.ymd >= fromYmd && day.ymd <= toYmd ? 'in-selection' : '';
		});
	}

	clearSelection() {
		this._eachDay(day => {
			day.classes[7] = '';
		});
	}

	getWeeks(forMonth) {
		let monthNumber = forMonth.format('M');
		let todayYmd = moment().format('YYYYMMDD');
		let firstDay = forMonth.clone().startOf('month').startOf('week');
		let lastDay = forMonth.clone().endOf('month').endOf('week');
		let weeks = [];
		let days = [];
		let currDay = firstDay.clone();

		let count = 0;
		while (currDay <= lastDay) {
			let ymd = currDay.format('YYYYMMDD');
			days.push({
				date: currDay.clone(),
				number: currDay.format('D'),
				ymd: ymd,
				classes: [
					'day-num',
					[0, 6].indexOf(count % 7) > -1 ? 'weekend' : 'weekday',
					monthNumber == currDay.format('M') ? '' : 'context',
					todayYmd == ymd ? 'today' : '',
					'', // in-range
					'', // start-of-range
					'', // end-of-range
					'', // in-selection
				],
			});
			currDay.add(1, 'day');
			if (count % 7 == 6) {
				// restart week after saturday
				weeks.push(days);
				days = [];
			}
			count++;
		}
		forMonth.weeks = weeks;
		return weeks;
	}
}
